import { useEffect, useState } from "react";
import CustomButton from "../../components/CustomButton";
import { useTranslation } from "react-i18next";
import Modal from "../../components/Modal";
import classes from "../../components/Modal.module.css";
import ErrorMessage from "../../components/ErrorMessage";
import saveIcon from "../../assets/save_icon.png";
import CustomInput from "../../components/CustomInput";
import CustomInputWithUnit from "../../components/CustomInputWithUnit";
import { onlyNums } from "../../utils/OnlyNums";
import Textarea from "../../components/Textarea";
import ErrorBox from "../../components/ErrorBox";
import PortalDropdown from "../../components/PortalDropdown";
import Loader, { LOADER_SIZES } from "../../components/Loader";
import axiosClient from "../../services/axiosClient";
import HotdropdownV2 from "../../components/HotdropdownV2";

export default function AddEmployeeEntitlementModal({ onClose, onSuccess, employeeId }) {
  const { t } = useTranslation("general");
  const [loadingModal, setLoadingModal] = useState(false);
  const [error, setError] = useState(null);
  const [validationError, setValidationError] = useState(null);
  const [entitlement, setEntitlement] = useState({
    entitlement: "",
    date: "",
    disbursementDate: "",
    amount: 0,
    notes: "",
  });
  const [selectedEntitlement, setSelectedEntitlement] = useState({
    id: null,
    name: "",
  });
  const [entitlements, setEntitlements] = useState([]);

  const getEntitlements = async (currentPage, limit, _, query) => {
    try {
      const response = await axiosClient.get("/entitlements/getEntitlements", {
        params: {
          limit,
          page: currentPage,
          name: query,
        },
      });

      const entitlements = response.data.entitlements;

      setEntitlements(entitlements);
    } catch (error) {
      console.error(
        "Error fetching entitlements:",
        error.response?.data || error
      );
    }
  };

  const handleCancel = () => {
    onClose();
  };

  const handleAddEntitlement = async () => {
    if (!selectedEntitlement.id) {
      setValidationError("entitlementRequired");
      return;
    }

    if (!entitlement.date) {
      setValidationError("dateRequired");
      return;
    }

    if (!entitlement.disbursementDate) {
      setValidationError("disbursementDateRequired");
      return;
    }

    try {
      setLoadingModal(true);
      setError(null);

      const response = await axiosClient.post("/userEntitlements/createUserEntitlement", {
        ...entitlement,
        notes: entitlement.notes,
        disbursementDate: entitlement.disbursementDate,
        userId: Number(employeeId),
        entitlementId: selectedEntitlement.id,
      });

      onSuccess();
    } catch (err) {
      console.error("Error creating entitlement:", err);
      setError("entitlementCreationFailed");
    } finally {
      setLoadingModal(false);
    }
  };

  return (
    <>
      <Modal
        title="addEntitlement"
        onClose={handleCancel}
        modalSize={"c-modal--small"}
        footer={
          <>
            {loadingModal ? <Loader size={LOADER_SIZES.MEDIUM} /> : null}
            {error ? (
              <ErrorMessage classList="o-row o-row--horizontal-align_end">
                {t(error)}
              </ErrorMessage>
            ) : null}
            {validationError ? (
              <ErrorMessage classList="o-row o-row--horizontal-align_end">
                {t(validationError)}
              </ErrorMessage>
            ) : null}
            <section className={classes.actions}>
              <CustomButton
                onClick={handleAddEntitlement}
                title={t("confirm")}
                icon={saveIcon}
                iconSize={16}
                disabled={loadingModal}
              />
              <CustomButton
                title={t("cancel")}
                onClick={handleCancel}
                variant="blank"
              />
            </section>
          </>
        }
      >
        {error ? (
          <ErrorBox classList="o-row o-row--horizontal-align_end">
            {t(error)}
          </ErrorBox>
        ) : null}
        {validationError ? (
          <ErrorBox classList="o-row o-row--horizontal-align_end">
            {t(validationError)}
          </ErrorBox>
        ) : null}
        <div className="o-column o-column--gap-size_400">
          <div>
            <label>{t("entitlement")}</label>
            <HotdropdownV2
              initOptions={entitlements}
              selected={selectedEntitlement}
              setSelected={(item) => {
                if (validationError === "entitlementRequired") {
                  setValidationError(null);
                }
                setSelectedEntitlement(item);
                setEntitlement((prev) => ({
                  ...prev,
                  entitlement: item.name,
                }));
              }}
              flex={true}
              errorText={
                validationError === "entitlementRequired"
                  ? "entitlementRequired"
                  : ""
              }
              getData={getEntitlements}
            />
          </div>

          <CustomInput
            title={t("date")}
            name="date"
            type="date"
            value={entitlement.date}
            onChange={(e) => {
              if (validationError === "dateRequired") {
                setValidationError(null);
              }
              setEntitlement((prev) => ({
                ...prev,
                date: e.target.value,
              }));
            }}
            flex={true}
            errorText={
              validationError === "dateRequired" ? t("dateRequired") : ""
            }
          />

          <CustomInput
            title={t("disbursementDate")}
            name="disbursementDate"
            type="date"
            value={entitlement.disbursementDate}
            onChange={(e) => {
              if (validationError === "disbursementDateRequired") {
                setValidationError(null);
              }
              setEntitlement((prev) => ({
                ...prev,
                disbursementDate: e.target.value,
              }));
            }}
            flex={true}
            errorText={
              validationError === "disbursementDateRequired"
                ? t("disbursementDateRequired")
                : ""
            }
          />

          <CustomInputWithUnit
            name="amount"
            title={t("amount")}
            value={selectedEntitlement.price}
            onKeyDown={onlyNums}
            flex={true}
            min={0}
            max={100}
            unit="IQD"
            type="moneyV2"
          />

          <div className="">
            <label>{t("notes")}</label>
            <Textarea
              name="notes"
              value={entitlement.notes}
              onChange={(e) => {
                setEntitlement((prev) => ({
                  ...prev,
                  notes: e.target.value,
                }));
              }}
              flex={true}
            />
          </div>
        </div>
      </Modal>
    </>
  );
}
